import React, { useState, useEffect, useCallback } from "react"
import { Users, UserPlus, Repeat, Building2, User, Wallet, Gem, RotateCcw, HeartHandshake, Info, ArrowRight } from "lucide-react"
import { Card, CardContent } from "../../../components/ui/card"
import { TiltCard } from "../../../components/ui/TiltCard"
import { computeCustomerKPIs, computeCustomerValueMetrics } from "../../../utils/dashboardAnalyticsHelpers"

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0)
const formatChange = (change) => (change === null || change === undefined ? null : `${change > 0 ? "+" : ""}${change}%`)

const CustomerOverviewSection = ({ orgId, dateRange, onNavigateTab, showReportLink = true }) => {
  const [kpis, setKpis] = useState(null)
  const [value, setValue] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    const [kpiData, valueData] = await Promise.all([
      computeCustomerKPIs(orgId, dateRange),
      computeCustomerValueMetrics(orgId, dateRange),
    ])
    setKpis(kpiData)
    setValue(valueData)
    setLoading(false)
  }, [orgId, dateRange])

  useEffect(() => {
    load()
  }, [load])

  const customerKPIs = [
    { label: "Total Customers", value: (kpis?.totalCustomers?.total ?? 0).toLocaleString(), change: formatChange(kpis?.totalCustomers?.change), icon: Users },
    { label: "New Customers", value: (kpis?.newCustomers?.total ?? 0).toLocaleString(), change: formatChange(kpis?.newCustomers?.change), icon: UserPlus },
    { label: "Returning Customers", value: (kpis?.returningCustomers?.total ?? 0).toLocaleString(), change: formatChange(kpis?.returningCustomers?.change), icon: Repeat },
    { label: "Organizations", value: (kpis?.organizations?.total ?? 0).toLocaleString(), change: formatChange(kpis?.organizations?.change), icon: Building2 },
    { label: "Individuals", value: (kpis?.individuals?.total ?? 0).toLocaleString(), change: formatChange(kpis?.individuals?.change), icon: User },
  ]

  const valueMetrics = [
    { label: "Average Spend", value: formatCurrency(value?.averageSpend), icon: Wallet },
    { label: "Lifetime Value", value: formatCurrency(value?.lifetimeValue), icon: Gem },
    { label: "Repeat Purchase Rate", value: `${value?.repeatPurchaseRate ?? 0}%`, icon: RotateCcw },
    { label: "Customer Retention", value: `${value?.retentionRate ?? 0}%`, icon: HeartHandshake },
  ]

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-foreground">Customers</h2>
        {showReportLink && (
          <button
            onClick={() => onNavigateTab?.("commerce-customers")}
            className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            See Full Report
            <ArrowRight className="size-3.5" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
          {Array.from({ length: 5 })?.map((_, i) => <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
          {customerKPIs.map((kpi) => {
            const Icon = kpi.icon
            const isPositive = kpi.change && !kpi.change.startsWith("-")
            return (
              <TiltCard key={kpi.label} onClick={() => onNavigateTab?.("commerce-customers")} tiltStrength={6}>
                <Card className="shadow-none h-full">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-2">
                      <Icon className="size-4" />
                      <span className="text-sm font-medium">{kpi.label}</span>
                    </div>
                    <p className="text-2xl font-semibold tracking-tight text-foreground">{kpi.value}</p>
                    {kpi.change && (
                      <p className={`text-xs font-medium mt-1 ${isPositive ? "text-green-600" : "text-destructive"}`}>
                        {kpi.change} vs prior period
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TiltCard>
            )
          })}
        </div>
      )}

      <h3 className="text-sm font-semibold text-muted-foreground mb-3">Customer Value</h3>
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {Array.from({ length: 4 })?.map((_, i) => <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {valueMetrics.map((metric) => {
            const Icon = metric.icon
            return (
              <TiltCard key={metric.label} onClick={() => onNavigateTab?.("commerce-customers")} tiltStrength={6}>
                <Card className="shadow-none h-full">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-2">
                      <Icon className="size-4" />
                      <span className="text-sm font-medium">{metric.label}</span>
                    </div>
                    <p className="text-2xl font-semibold tracking-tight text-foreground">{metric.value}</p>
                  </CardContent>
                </Card>
              </TiltCard>
            )
          })}
        </div>
      )}

      {!loading && kpis?.organizations?.total > 0 && (
        <Card className="shadow-none border-dashed">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="size-4 text-muted-foreground mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              {kpis.organizations.total} organization{kpis.organizations.total === 1 ? "" : "s"} shown above are counted as single customers,
              but each can represent many learners on Nesolearn. Individual learner counts are tracked separately under Learners.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default CustomerOverviewSection